/**
 * Building Editor Page
 *
 * Full-screen map interface for creating and editing hangars.
 * Uses flex layout: Map on left (flex-1), Panel on right (420px when visible).
 */

import { useEffect, useRef, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { BuildingEditorMap } from '@/components/builder/BuildingEditorMap'
import { BuildingEditorPanel } from '@/components/builder/BuildingEditorPanel'
import { ObstacleSidePanel } from '@/components/builder/obstacles/ObstacleSidePanel'
import { SubunitSidePanel } from '@/components/builder/subunits/SubunitSidePanel'
import { useAppContext, useMapBuildings } from '@/hooks'
import { useTenant } from '@/contexts'
import { useBuildingEditorStore } from '@/stores/buildingEditorStore'
import { useObstacleStore } from '@/stores/obstacleStore'
import { useSubunitStore } from '@/stores/subunitStore'
import { useObstaclesByLocation } from '@/hooks/useObstacles'
import { useSubunitsByLocation } from '@/hooks/useSubunits'
import { fetchBuildingWithUnit } from '@/lib/api/hangars'
import { generateAreaCode } from '@/lib/utils/areaCodeGenerator'
import { toast } from 'sonner'
import { Loader2 } from 'lucide-react'

export function BuildingEditorPage() {
  // Get location from URL context
  const { locationId, isValidLocation } = useAppContext()
  const { currentOrganization } = useTenant()
  const reset = useBuildingEditorStore((state) => state.reset)
  const loadBuildingForDuplicate = useBuildingEditorStore((state) => state.loadBuildingForDuplicate)
  const mapCenter = useBuildingEditorStore((state) => state.mapCenter)
  const mode = useBuildingEditorStore((state) => state.mode)
  const [searchParams, setSearchParams] = useSearchParams()

  // Obstacle state for side panel
  const selectedObstacleId = useObstacleStore((s) => s.selectedObstacleId)
  const obstaclesVisible = useObstacleStore((s) => s.obstaclesVisible)
  const isObstacleCreatingMode = useObstacleStore((s) => s.isCreatingMode)
  const selectObstacle = useObstacleStore((s) => s.selectObstacle)
  const { data: locationObstacles } = useObstaclesByLocation(locationId)
  const selectedObstacle = locationObstacles?.find((o) => o.id === selectedObstacleId) ?? null
  const showObstacleSidePanel = mode === 'view' && obstaclesVisible && (selectedObstacle || isObstacleCreatingMode)

  // Subunit state for side panel
  const selectedSubunitId = useSubunitStore((s) => s.selectedSubunitId)
  const subunitsVisible = useSubunitStore((s) => s.subunitsVisible)
  const isSubunitCreatingMode = useSubunitStore((s) => s.isCreatingMode)
  const selectSubunit = useSubunitStore((s) => s.selectSubunit)
  const { data: locationSubunits } = useSubunitsByLocation(locationId)
  const selectedSubunit = locationSubunits?.find((s) => s.id === selectedSubunitId) ?? null
  const showSubunitSidePanel = mode === 'view' && subunitsVisible && (selectedSubunit || isSubunitCreatingMode) && !showObstacleSidePanel
  const { buildings } = useMapBuildings(locationId)

  // Use ref to avoid cleanup running on every reset identity change
  const resetRef = useRef(reset)

  // Keep ref updated with latest function reference
  useEffect(() => {
    resetRef.current = reset
  }, [reset])

  // Reset editor state when leaving the page (unmount only)
  useEffect(() => {
    return () => {
      resetRef.current()
    }
  }, []) // Empty deps = only runs cleanup on actual unmount

  // Handle ?duplicate=<sourceId> query param
  const duplicateHandled = useRef(false)
  useEffect(() => {
    const duplicateId = searchParams.get('duplicate')
    if (!duplicateId || duplicateHandled.current) return
    if (!currentOrganization?.id || !isValidLocation || !mapCenter) return

    duplicateHandled.current = true

    const doDuplicate = async () => {
      try {
        // Fetch source building to determine its type
        const { building } = await fetchBuildingWithUnit(duplicateId)
        const buildingType = building.type || 'single_unit_hangar'

        // Generate a unique area code
        const newCode = await generateAreaCode(currentOrganization.id, buildingType)
        const newName = `${building.name} (Copy)`

        await loadBuildingForDuplicate(duplicateId, newCode, newName)
      } catch (error) {
        console.error('Failed to duplicate building:', error)
        toast.error('Failed to duplicate building')
      }

      // Clear the query param
      setSearchParams({}, { replace: true })
    }

    doDuplicate()
  }, [searchParams, currentOrganization?.id, isValidLocation, mapCenter, loadBuildingForDuplicate, setSearchParams])

  // Handle duplicate from map label gear menu
  const handleDuplicateFromMap = useCallback(async (buildingId: string) => {
    if (!currentOrganization?.id) return

    try {
      const { building } = await fetchBuildingWithUnit(buildingId)
      const buildingType = building.type || 'single_unit_hangar'
      const newCode = await generateAreaCode(currentOrganization.id, buildingType)
      const newName = `${building.name} (Copy)`

      await loadBuildingForDuplicate(buildingId, newCode, newName)
    } catch (error) {
      console.error('Failed to duplicate building:', error)
      toast.error('Failed to duplicate building')
    }
  }, [currentOrganization?.id, loadBuildingForDuplicate])

  // Show loading state while location is being validated
  if (!isValidLocation || !locationId) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-2">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <span className="text-sm text-muted-foreground">Loading location...</span>
        </div>
      </div>
    )
  }

  return (
    <div className="relative flex h-full w-full">
      {/* Map takes remaining width */}
      <div className="relative flex-1">
        <BuildingEditorMap locationId={locationId} onDuplicateClick={handleDuplicateFromMap} />
      </div>

      {/* Right panel for editing — absolute overlay that slides in from the right
          (does not resize the map, so the scene never flickers on open/close).
          Stays mounted; parks off-screen via transform when mode === 'view'. */}
      <BuildingEditorPanel locationId={locationId} />

      {/* Obstacle side panel — view mode only (mutual exclusion with BuildingEditorPanel) */}
      {showObstacleSidePanel && (
        <ObstacleSidePanel
          obstacle={selectedObstacle}
          onClose={() => {
            selectObstacle(null)
            useObstacleStore.getState().cancelCreating()
          }}
        />
      )}

      {/* Subunit side panel — view mode only (mutual exclusion with obstacle panel) */}
      {showSubunitSidePanel && (
        <SubunitSidePanel
          subunit={selectedSubunit}
          buildings={buildings}
          onClose={() => {
            selectSubunit(null)
            useSubunitStore.getState().cancelCreating()
          }}
        />
      )}
    </div>
  )
}
